import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type { Driver, Paginated, Partner, Place, SearchHit, Vehicle } from '../types';

/** ─── Контрагенти ─────────────────────────────────────────────────────── */

export function usePartners(q?: string) {
  return useQuery({
    queryKey: qk.partners.list(q),
    queryFn: () => api.get<Paginated<Partner>>('/partners', { q, perPage: 200 }),
    staleTime: 5 * 60_000,
  });
}

export function useSavePartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Partner> & { id?: string }) =>
      id ? api.patch<Partner>(`/partners/${id}`, body) : api.post<Partner>('/partners', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.partners.all }),
  });
}

/** ─── Места ───────────────────────────────────────────────────────────── */

/**
 * Предложения за адрес. Запазените места излизат първи и това е нарочно:
 * при ~52 товара месечно над повтарящ се набор адреси, закачено веднъж място
 * бие всеки геокодер завинаги.
 */
export function usePlaceSuggestions(q: string, country?: string) {
  return useQuery({
    queryKey: qk.places.suggest(q, country),
    queryFn: () => api.get<Place[]>('/places/search', { q, country, limit: 8 }),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
    meta: { silent: true },
  });
}

/** ─── Бусове ──────────────────────────────────────────────────────────── */

/** Един бус — с товароносимостта, сроковете и картите за гориво. */
export function useVehicle(id: string | undefined) {
  return useQuery({
    queryKey: qk.vehicles.detail(id ?? ''),
    queryFn: () => api.get<Vehicle>(`/vehicles/${id}`),
    enabled: Boolean(id),
  });
}

export function useVehicles() {
  return useQuery({
    queryKey: qk.vehicles.list(),
    queryFn: () => api.get<Paginated<Vehicle>>('/vehicles', { perPage: 100 }),
    staleTime: 5 * 60_000,
  });
}

/**
 * Само полетата, които формата наистина редактира. Изгледът на буса носи и
 * изчислени стойности — товароносимостта F.2 − G, текущия курс, картите — и
 * върнати обратно те щяха да се запишат като ръчно въведени.
 */
const VEHICLE_FIELDS = [
  'fleetNo',
  'plate',
  'vin',
  'make',
  'model',
  'year',
  'status',
  'permissibleMassKg',
  'technicalMassKg',
  'kerbMassKg',
  'cargoVolumeM3',
  'cargoLengthCm',
  'cargoWidthCm',
  'cargoHeightCm',
  'euroStandard',
  'fuelNormL100kmX100',
  'hasTailLift',
  'hasAdrEquipment',
  'notes',
] as const;

function vehicleBody(draft: Partial<Vehicle>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const key of VEHICLE_FIELDS) {
    if (draft[key] !== undefined) body[key] = draft[key];
  }
  /** Ръчната товароносимост е отделно поле; изчислената не се праща. */
  if (draft.payloadManualKg !== undefined) body.payloadKg = draft.payloadManualKg;
  return body;
}

export function useSaveVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...draft }: Partial<Vehicle> & { id?: string }) =>
      id
        ? api.patch<Vehicle>(`/vehicles/${id}`, vehicleBody(draft))
        : api.post<Vehicle>('/vehicles', vehicleBody(draft)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.vehicles.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
    },
  });
}

/** ─── Шофьори ─────────────────────────────────────────────────────────── */

export function useDrivers() {
  return useQuery({
    queryKey: qk.drivers.list(),
    queryFn: () => api.get<Paginated<Driver>>('/drivers', { perPage: 100 }),
    staleTime: 5 * 60_000,
  });
}

export function useSaveDriver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Driver> & { id?: string }) =>
      id ? api.patch<Driver>(`/drivers/${id}`, body) : api.post<Driver>('/drivers', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.drivers.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
    },
  });
}

/** ─── Геозони ─────────────────────────────────────────────────────────── */

export interface Geofence {
  id: string;
  name: string;
  kind: 'base' | 'customer' | 'parking' | 'fuel' | 'border' | 'restricted';
  shape: 'circle' | 'polygon';
  centerLatE7: number | null;
  centerLonE7: number | null;
  radiusM: number | null;
  polygon: Array<[number, number]> | null;
  isActive: boolean;
}

export function useGeofences() {
  return useQuery({
    queryKey: qk.geofences.list(),
    queryFn: () => api.get<Paginated<Geofence>>('/geofences', { perPage: 200 }),
    staleTime: 10 * 60_000,
  });
}

/** ─── Глобално търсене ────────────────────────────────────────────────── */

export function useGlobalSearch(q: string) {
  return useQuery({
    queryKey: qk.search(q),
    queryFn: () => api.get<SearchHit[]>('/search', { q, limit: 20 }),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
    meta: { silent: true },
  });
}
